import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = {
  name: "", category: "excavadora", sector: "construccion", listing_type: "alquiler",
  brand: "", model: "", year: 2023, description: "", specs: {},
  price_rent_day: "", price_sale: "", currency: "USD", availability: "disponible",
  location: { lat: -12.0464, lng: -77.0428, address: "", city: "Lima" },
  images: [""], featured: false,
};

const CATEGORIES = [["excavadora", "Excavadora"], ["cargador", "Cargador"], ["camion_minero", "Camión Minero"], ["retroexcavadora", "Retroexcavadora"], ["grua", "Grúa"], ["compactadora", "Compactadora"], ["otro", "Otro"]];

function MachineryForm({ initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [specsText, setSpecsText] = useState(Object.entries(initial.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n"));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setLoc = (k) => (e) => setForm({ ...form, location: { ...form.location, [k]: k === "lat" || k === "lng" ? parseFloat(e.target.value) : e.target.value } });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const specs = {};
    specsText.split("\n").forEach((l) => { const [k, ...v] = l.split(":"); if (k && v.length) specs[k.trim()] = v.join(":").trim(); });
    const payload = {
      ...form, specs,
      year: form.year ? parseInt(form.year) : null,
      price_rent_day: form.price_rent_day === "" ? null : parseFloat(form.price_rent_day),
      price_sale: form.price_sale === "" ? null : parseFloat(form.price_sale),
      images: form.images.filter(Boolean),
    };
    try {
      if (initial.id) await api.put(`/machinery/${initial.id}`, payload);
      else await api.post("/machinery", payload);
      toast.success("Maquinaria guardada");
      onSaved();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
      <div><Label>Nombre *</Label><Input required value={form.name} onChange={set("name")} data-testid="mach-name" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Marca</Label><Input value={form.brand} onChange={set("brand")} /></div>
        <div><Label>Modelo</Label><Input value={form.model} onChange={set("model")} /></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Categoría</Label>
          <select value={form.category} onChange={set("category")} className="w-full h-10 px-2 bg-background border border-input text-sm">
            {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div><Label>Sector</Label>
          <select value={form.sector} onChange={set("sector")} className="w-full h-10 px-2 bg-background border border-input text-sm">
            <option value="construccion">Construcción</option><option value="mineria">Minería</option>
          </select>
        </div>
        <div><Label>Modalidad</Label>
          <select value={form.listing_type} onChange={set("listing_type")} className="w-full h-10 px-2 bg-background border border-input text-sm">
            <option value="alquiler">Alquiler</option><option value="venta">Venta</option><option value="ambos">Ambos</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Año</Label><Input type="number" value={form.year} onChange={set("year")} /></div>
        <div><Label>Precio/día</Label><Input type="number" value={form.price_rent_day} onChange={set("price_rent_day")} /></div>
        <div><Label>Precio venta</Label><Input type="number" value={form.price_sale} onChange={set("price_sale")} /></div>
      </div>
      <div><Label>Disponibilidad</Label>
        <select value={form.availability} onChange={set("availability")} className="w-full h-10 px-2 bg-background border border-input text-sm">
          <option value="disponible">Disponible</option><option value="alquilado">Alquilado</option><option value="vendido">Vendido</option><option value="mantenimiento">Mantenimiento</option>
        </select>
      </div>
      <div><Label>Descripción</Label><Textarea rows={2} value={form.description} onChange={set("description")} /></div>
      <div><Label>URL de imagen</Label><Input value={form.images[0]} onChange={(e) => setForm({ ...form, images: [e.target.value] })} /></div>
      <div><Label>Especificaciones (una por línea: Clave: Valor)</Label><Textarea rows={3} value={specsText} onChange={(e) => setSpecsText(e.target.value)} placeholder={"Potencia: 268 HP\nPeso: 36000 kg"} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Ciudad</Label><Input value={form.location.city} onChange={setLoc("city")} /></div>
        <div><Label>Dirección</Label><Input value={form.location.address} onChange={setLoc("address")} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Latitud</Label><Input type="number" step="any" value={form.location.lat} onChange={setLoc("lat")} /></div>
        <div><Label>Longitud</Label><Input type="number" step="any" value={form.location.lng} onChange={setLoc("lng")} /></div>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Destacado</label>
      <button type="submit" disabled={saving} data-testid="mach-save" className="w-full h-11 bg-brand text-black font-semibold hover:bg-brand-hover transition-colors">{saving ? "Guardando..." : "Guardar"}</button>
    </form>
  );
}

export default function AdminMachinery() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/machinery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onSaved = () => { setOpen(false); setEditing(null); load(); };
  const del = async (id) => { if (!window.confirm("¿Eliminar esta maquinaria?")) return; await api.delete(`/machinery/${id}`); toast.success("Eliminada"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-black text-3xl tracking-tight">MAQUINARIA</h1>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <button onClick={() => setEditing(null)} data-testid="add-machinery-btn" className="flex items-center gap-2 px-5 py-3 bg-brand text-black font-semibold hover:bg-brand-hover transition-colors"><Plus size={18} /> Agregar</button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar" : "Nueva"} maquinaria</DialogTitle></DialogHeader>
            <MachineryForm initial={editing || EMPTY} onSaved={onSaved} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border overflow-x-auto" data-testid="admin-machinery-table">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left"><tr>
            <th className="p-3 font-semibold">Equipo</th><th className="p-3 font-semibold">Categoría</th><th className="p-3 font-semibold">Sector</th><th className="p-3 font-semibold">Estado</th><th className="p-3 font-semibold">Precio</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="border-t border-border">
                <td className="p-3 font-medium">{m.name}<div className="text-xs text-muted-foreground">{m.brand} {m.model}</div></td>
                <td className="p-3 capitalize">{m.category.replace("_", " ")}</td>
                <td className="p-3 capitalize">{m.sector}</td>
                <td className="p-3 capitalize">{m.availability}</td>
                <td className="p-3">{m.price_rent_day ? `${m.currency} ${m.price_rent_day}/día` : m.price_sale ? `${m.currency} ${m.price_sale.toLocaleString()}` : "-"}</td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => { setEditing(m); setOpen(true); }} data-testid={`edit-${m.id}`} className="p-2 hover:text-brand"><Pencil size={16} /></button>
                  <button onClick={() => del(m.id)} data-testid={`delete-${m.id}`} className="p-2 hover:text-destructive"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
